import React from "react";
import PropTypes from "prop-types";
import { MdCheck } from "react-icons/lib/md";
import SelectDropdown from "components/forms/select-dropdown";

// Dispositions where the call connected and quality metrics apply
const CONNECTED_DISPOSITIONS = [
  "Declined Supplies",
  "Did Not Disposition",
  "Ordered Supplies",
  "Patient Ended Call",
  "Patient Requests No Contact",
  "PT Requested Call Back",
  "Transferred",
  "Follow Up"
];

const DATE_PRESETS = [
  { key: "last7", label: "Last 7 Days" },
  { key: "last30", label: "Last 30 Days" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" }
];

const CallQualityFilters = ({
  filters,
  filterOptions,
  onFilterChange,
  onApply,
  onDatePreset,
  onExport,
  isFetching
}) => {
  const handleDispositionToggle = (disposition) => {
    const currentDispositions = filters.dispositions || [];
    const isSelected = currentDispositions.includes(disposition);

    let newDispositions;
    if (isSelected) {
      newDispositions = currentDispositions.filter(d => d !== disposition);
    } else {
      newDispositions = [...currentDispositions, disposition];
    }

    onFilterChange("dispositions", newDispositions);
  };

  const handleSelectAll = () => {
    const allDispositions = filterOptions?.dispositions?.map(d => d.value) || [];
    onFilterChange("dispositions", allDispositions);
  };

  const handleDeselectAll = () => {
    onFilterChange("dispositions", []);
  };

  const handleSelectConnected = () => {
    const connectedDisps = filterOptions?.dispositions
      ?.filter(d => CONNECTED_DISPOSITIONS.includes(d.text))
      ?.map(d => d.value) || [];
    onFilterChange("dispositions", connectedDisps);
  };

  const handleSelectIncomplete = () => {
    const incompleteDisps = filterOptions?.dispositions
      ?.filter(d => !CONNECTED_DISPOSITIONS.includes(d.text))
      ?.map(d => d.value) || [];
    onFilterChange("dispositions", incompleteDisps);
  };

  const dispositions = filterOptions?.dispositions || [];
  const selectedDispositions = filters.dispositions || [];

  return (
    <div className="call-quality-filters">
      {(onDatePreset || onExport) && (
        <div className="date-presets">
          <span className="presets-label">Quick Date Range:</span>
          <div className="presets-buttons">
            {DATE_PRESETS.map(preset => (
              <button
                key={preset.key}
                type="button"
                className="preset-button"
                onClick={() => onDatePreset(preset.key)}
                disabled={isFetching}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {onExport && (
            <button
              className="button secondary export-button"
              onClick={onExport}
              disabled={isFetching}
            >
              Export CSV
            </button>
          )}
        </div>
      )}
      <div className="filters-row">
        <div className="filter-group">
          <label>Agent Type</label>
          <SelectDropdown
            options={[
              { value: "", text: "All" },
              { value: "agent", text: "Agent" },
              { value: "live", text: "Live" }
            ]}
            value={filters.agentType}
            onChange={(value) => onFilterChange("agentType", value)}
            placeHolder="All"
            disabled={isFetching}
          />
        </div>

        <div className="filter-group">
          <label>Company</label>
          <SelectDropdown
            options={[
              { value: "", text: "All Companies" },
              ...(filterOptions?.companies || [])
            ]}
            value={filters.companyGuid}
            onChange={(value) => onFilterChange("companyGuid", value)}
            placeHolder="All Companies"
            disabled={isFetching}
          />
        </div>

        <div className="filter-group">
          <label>Team</label>
          <SelectDropdown
            options={[
              { value: "", text: "All Teams" },
              ...(filterOptions?.teams || [])
            ]}
            value={filters.teamGuid}
            onChange={(value) => onFilterChange("teamGuid", value)}
            placeHolder="All Teams"
            disabled={isFetching}
          />
        </div>

        <div className="filter-group">
          <label>Coach</label>
          <SelectDropdown
            options={[
              { value: "", text: "All Coaches" },
              ...(filterOptions?.coaches || [])
            ]}
            value={filters.coachGuid}
            onChange={(value) => onFilterChange("coachGuid", value)}
            placeHolder="All Coaches"
            disabled={isFetching}
          />
        </div>

        <div className="filter-group">
          <label>Patient Account #</label>
          <div className="account-input-container">
            <input
              type="text"
              className="account-input"
              value={filters.patientSearch || ""}
              onChange={(e) => onFilterChange("patientSearch", e.target.value)}
              placeholder=""
              disabled={isFetching}
            />
          </div>
        </div>

        <div className="filter-group filter-actions">
          <button
            className="button primary"
            onClick={onApply}
            disabled={isFetching}
          >
            Apply Filters
          </button>
        </div>
      </div>

      {dispositions.length > 0 && (
        <div className="disposition-filters">
          <div className="disposition-header">
            <span className="disposition-label">Filter by Disposition</span>
            <div className="disposition-presets">
              <button
                type="button"
                className="preset-button"
                onClick={handleSelectAll}
                disabled={isFetching}
              >
                All
              </button>
              <button
                type="button"
                className="preset-button"
                onClick={handleSelectConnected}
                disabled={isFetching}
              >
                Connected Only
              </button>
              <button
                type="button"
                className="preset-button"
                onClick={handleSelectIncomplete}
                disabled={isFetching}
              >
                Incomplete Only
              </button>
              <button
                type="button"
                className="preset-button"
                onClick={handleDeselectAll}
                disabled={isFetching}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Connected Calls - Quality metrics apply */}
          {(() => {
            const connectedDisps = dispositions.filter(d => CONNECTED_DISPOSITIONS.includes(d.text));
            if (connectedDisps.length === 0) return null;
            return (
              <div className="disposition-group">
                <div className="disposition-group-header">
                  <span className="group-title">Connected Calls</span>
                  <span className="group-description">Patient answered — quality metrics apply</span>
                </div>
                <div className="disposition-chips">
                  {connectedDisps.map(option => {
                    const isSelected = selectedDispositions.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`disposition-chip ${isSelected ? "selected" : ""}`}
                        onClick={() => handleDispositionToggle(option.value)}
                        disabled={isFetching}
                      >
                        <MdCheck className={`chip-check ${isSelected ? "visible" : ""}`} />
                        {option.text}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Incomplete Calls - No quality metrics */}
          {(() => {
            const incompleteDisps = dispositions.filter(d => !CONNECTED_DISPOSITIONS.includes(d.text));
            if (incompleteDisps.length === 0) return null;
            return (
              <div className="disposition-group">
                <div className="disposition-group-header">
                  <span className="group-title">Incomplete Calls</span>
                  <span className="group-description">Voicemail, no answer, or failed — limited metrics</span>
                </div>
                <div className="disposition-chips">
                  {incompleteDisps.map(option => {
                    const isSelected = selectedDispositions.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`disposition-chip ${isSelected ? "selected" : ""}`}
                        onClick={() => handleDispositionToggle(option.value)}
                        disabled={isFetching}
                      >
                        <MdCheck className={`chip-check ${isSelected ? "visible" : ""}`} />
                        {option.text}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {selectedDispositions.length === 0 && (
            <div className="disposition-hint">
              No dispositions selected — showing all calls
            </div>
          )}
        </div>
      )}
    </div>
  );
};

CallQualityFilters.propTypes = {
  filters: PropTypes.shape({
    agentType: PropTypes.string,
    companyGuid: PropTypes.string,
    teamGuid: PropTypes.string,
    coachGuid: PropTypes.string,
    dispositions: PropTypes.arrayOf(PropTypes.string),
    patientSearch: PropTypes.string
  }).isRequired,
  filterOptions: PropTypes.shape({
    companies: PropTypes.array,
    teams: PropTypes.array,
    coaches: PropTypes.array,
    dispositions: PropTypes.array
  }),
  onFilterChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onDatePreset: PropTypes.func,
  onExport: PropTypes.func,
  isFetching: PropTypes.bool
};

export default CallQualityFilters;
